/**
 * ChartTab — Chart visualization with category-specific rendering.
 *
 * Handles:
 *  - Default bar chart (all categories)
 *  - Enrollment grouped bar chart
 *  - Infrastructure quantity comparison
 *  - Sanctioned Section aggregation
 */

import React from 'react';
import ChartView from '../ChartView';
import EnrollmentChartView from '../EnrollmentChartView';
import { applyFilters } from '../../utils/filterUtils';
import type { UseFiltersReturn } from '../../hooks/useFilters';
import type { UseKPIDataReturn } from '../../hooks/useKPIData';
import type { ChartDataContext } from '../../categories/CategoryConfig';

interface ChartTabProps {
  filters: UseFiltersReturn;
  kpiData: UseKPIDataReturn;
}

export default function ChartTab({ filters, kpiData }: ChartTabProps) {
  const { state, region, category, kpi, selectedInfraItems, globalFilters } = filters;
  const {
    categoryList, kpiList,
    kpiDataLoading, kpiDataError,
    kpiDataItems, filteredAggregatedData, filteredStatesList,
    isInfra, infraPivotedSchools,
    categoryConfig,
  } = kpiData;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 min-h-[800px]">
      {/* Status text */}
      <div className="mb-4 flex flex-col gap-2">
        {!category && !kpi && !state && !region && (
          <p className="text-sm text-gray-500">Showing all records by state</p>
        )}
        {category && kpi && (
          <p className="text-sm text-green-600">
            Filtered by: {categoryList.find(c => c.key === category)?.label} - {kpiList.find(k => k.key === kpi)?.label}
          </p>
        )}
        {(state || region) && (
          <p className="text-sm text-blue-600">
            {state && `State: ${state}`}
            {state && region && ' | '}
            {region && `Region: ${region}`}
          </p>
        )}
      </div>

      {/* Loading */}
      {kpiDataLoading && (
        <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
          <p className="text-gray-500">Loading chart data...</p>
        </div>
      )}

      {/* Error */}
      {kpiDataError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
          <p className="text-red-700 text-sm">{kpiDataError}</p>
        </div>
      )}

      {/* No records with filters */}
      {!kpiDataLoading && !kpiDataError && category && kpi && globalFilters.length > 0 && filteredAggregatedData.length === 0 && (
        <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
          <div className="text-center p-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No records match the current filter criteria.</h3>
            <p className="text-sm text-gray-400">Try adjusting your filters to see chart data.</p>
          </div>
        </div>
      )}

      {/* Chart content */}
      {!kpiDataLoading && !kpiDataError && (!category || !kpi || !globalFilters.length || filteredAggregatedData.length > 0) && (
        <ChartContent
          category={category}
          kpi={kpi}
          kpiList={kpiList}
          kpiDataItems={kpiDataItems}
          filteredAggregatedData={filteredAggregatedData}
          filteredStatesList={filteredStatesList}
          globalFilters={globalFilters}
          isInfra={isInfra}
          selectedInfraItems={selectedInfraItems}
          infraPivotedSchools={infraPivotedSchools}
          categoryConfig={categoryConfig}
        />
      )}
    </div>
  );
}

// ─── Inner component to decide chart type ────────────────────

interface ChartContentProps {
  category: string;
  kpi: string;
  kpiList: Array<{ key: string; label: string }>;
  kpiDataItems: any[];
  filteredAggregatedData: any[];
  filteredStatesList: any[];
  globalFilters: any[];
  isInfra: boolean;
  selectedInfraItems: string[];
  infraPivotedSchools: any[];
  categoryConfig: ReturnType<typeof import('../../categories').getCategoryConfig>;
}

function ChartContent({
  category, kpi, kpiList, kpiDataItems,
  filteredAggregatedData, filteredStatesList, globalFilters,
  isInfra, selectedInfraItems, infraPivotedSchools,
  categoryConfig,
}: ChartContentProps) {
  const kpiLabel = kpiList.find(k => k.key === kpi)?.label || kpi || '';
  const filtered = globalFilters.length > 0;

  // ── Try category-specific chart from config ──
  if (categoryConfig.chart && kpi && kpiDataItems.length > 0) {
    const descriptor = categoryConfig.chart(kpi);
    if (descriptor) {
      const ctx: ChartDataContext = {
        kpi,
        kpiLabel,
        category,
        kpiDataItems,
        filteredAggregatedData,
        filteredStatesList,
        globalFilters,
        selectedInfraItems,
        infraPivotedSchools,
        applyFilters,
      };
      const data = descriptor.buildData(ctx);

      if (descriptor.type === 'groupedBar') {
        return (
          <EnrollmentChartView
            data={data}
            title={descriptor.title(kpiLabel, filtered)}
            xAxisLabel={descriptor.xAxisLabel || 'Category'}
            yAxisLabel={descriptor.yAxisLabel || 'Value'}
          />
        );
      }

      return (
        <ChartView
          data={data}
          chartType={descriptor.type}
          title={descriptor.title(kpiLabel, filtered)}
          xAxisLabel={descriptor.xAxisLabel || 'Category'}
          yAxisLabel={descriptor.yAxisLabel || 'Value'}
          kpiField="name"
        />
      );
    }
  }

  // ── Default bar chart (works for any category without a custom chart) ──
  return (
    <ChartView
      data={
        category && kpi && filteredAggregatedData.length > 0
          ? filteredAggregatedData.map(s => ({ name: s.stateName, value: s.schoolCount, stateID: s.stateID }))
          : filteredStatesList.map(s => ({ name: s.stateName, value: s.schoolCount, stateID: s.stateID }))
      }
      chartType="bar"
      title={
        category && kpi
          ? `${kpiLabel}${filtered ? ' (Filtered)' : ''}`
          : 'School Distribution by State'
      }
      xAxisLabel={category && kpi ? kpiLabel || 'Category' : 'State'}
      yAxisLabel="Records"
      kpiField={category && kpi ? kpi : undefined}
    />
  );
}
